import React, { useRef } from "react";

import { AgGridOverlay } from "@src/components/overlays/ag_grid_overlay";
import ResourcePlanOverviewToolBar from "@src/features/resource_plan_overview/components/resource_plan_overview_tool_bar/resource_plan_overview_tool_bar";
import { onFilter } from "@src/features/resource_plan_overview/utils/resource_plan_overview_filter_utils";
import { ActiveFilterBar } from "@src/features/table_filtering/components/active_filter_bar/index";
import { gatherActiveFilters } from "@src/features/table_filtering/utils/base_state_handler_utils";
import {
  clearResourcePlanOverviewState,
  clearResourcePlanOverviewFilter,
} from "@src/services/resourcePlanOverviewSlice";
import { useGetResourcePlansQuery } from "@src/services/slices/resourcePlanApi";
import { useAppSelector, useAppDispatch, RootState } from "@src/setupStore";
import "./resource_plan_overview_table.less";
import { ResourcePlanOverviewTableRowData } from "@src/types/resource_plan_types";
import { IRowNode } from "ag-grid-community";
import { GridApi, GridReadyEvent } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";

import { getResourcePlanOverviewGridOptions } from "../../utils/resource_plan_overview_grid_options";

const ResourcePlanOverviewTable: React.FC = () => {
  const gridApi = useRef<GridApi>(null);
  const dispatch = useAppDispatch();

  const { data: resourcePlans, isFetching } = useGetResourcePlansQuery();

  const { quickFilter, customTableFilters } = useAppSelector(
    (state: RootState) => state.resourcePlanOverviewSlice
  );

  /**
   * AG Grid Ready event
   *
   * @param event - AG Grid event parameter
   */
  function onGridReady(event: GridReadyEvent): void {
    gridApi.current = event.api;
  }

  /**
   * Reset active filters for the resource plan overview table.
   * @param key The key of the filter to reset.
   * @param value The value of the filter to reset.
   */
  function resetActiveFilters(key: string, value: string): void {
    dispatch(clearResourcePlanOverviewFilter({ key, value }));
  }

  return (
    <div className="resource-plan-overview-table">
      <ResourcePlanOverviewToolBar
        isFetching={isFetching}
        isDisabled={false}
        resourcePlans={resourcePlans}
      />
      <ActiveFilterBar
        activeFilters={gatherActiveFilters(customTableFilters)}
        onRemove={(key: string, value: string) =>
          resetActiveFilters(key, value)
        }
        onRemoveAll={() => dispatch(clearResourcePlanOverviewState())}
        isDisabled={false}
      />
      <AgGridReact
        rowData={resourcePlans}
        gridOptions={getResourcePlanOverviewGridOptions()}
        onGridReady={onGridReady}
        noRowsOverlayComponent={AgGridOverlay}
        noRowsOverlayComponentParams={{
          text: "No resource plan found",
          customStyle: { marginTop: "48px" },
        }}
        loadingOverlayComponent={AgGridOverlay}
        loadingOverlayComponentParams={{
          text: "Loading resource plans...",
          customStyle: { marginTop: "48px" },
        }}
        loading={isFetching}
        isExternalFilterPresent={() => true}
        doesExternalFilterPass={(
          node: IRowNode<ResourcePlanOverviewTableRowData>
        ) => {
          return onFilter(quickFilter, node, customTableFilters);
        }}
      />
    </div>
  );
};

export default ResourcePlanOverviewTable;
